"use client";

import { useEffect, useMemo, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { DetailPricingData } from "@/lib/checkout-geo-pricing";
import formatProgramPrice from "@/lib/checkout-geo-pricing/formatProgramPrice";
import { DEFAULT_USE_IN_VIEW_OPTIONS } from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import ActionCardWrapper from "./ActionCardWrapper";
import PriceDisplay from "./PriceDisplay";
import type { GeoPricingLocation } from "./types";

type SinglePricingCardProps = {
  pricingData: DetailPricingData;
  location: GeoPricingLocation;
};

export default function SinglePricingCard({
  pricingData,
  location,
}: SinglePricingCardProps) {
  const hasViewedRef = useRef(false);
  const { ref: inViewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);
  const { originalPrice, discountedPrice, currency, geoPricing } = pricingData;
  const placementRaw = geoPricing?.heroCardBadges?.placement?.toLowerCase();
  const placement: "start" | "end" | null =
    placementRaw === "start" || placementRaw === "end" ? placementRaw : null;
  const hasDiscount =
    originalPrice != null &&
    discountedPrice != null &&
    originalPrice !== discountedPrice;
  const displayText = hasDiscount
    ? `${formatProgramPrice(discountedPrice, currency)} ${formatProgramPrice(originalPrice, currency)}`
    : formatProgramPrice(discountedPrice ?? originalPrice, currency);

  const pricingCardWebElement = useMemo(
    () => ({
      elementType: "TEXT" as const,
      htmlId: "geo-pricing-card",
      location,
      name: "geo-pricing-card",
      text: displayText,
    }),
    [location, displayText],
  );

  const { elementViewed } = useElementEvents({
    webElement: pricingCardWebElement,
  });

  useEffect(() => {
    if (inView && !hasViewedRef.current) {
      hasViewedRef.current = true;
      elementViewed();
    }
  }, [inView, elementViewed]);

  return (
    <div
      ref={inViewRef}
      className="bg-white rounded-xl border border-gray-200 flex flex-col items-center shadow-sm"
    >
      <ActionCardWrapper
        pricingData={pricingData}
        badgesPlacement={placement}
        location={location}
        className="w-full flex flex-col items-center gap-2 px-1 py-4"
      >
        <PriceDisplay
          originalPrice={originalPrice}
          discountedPrice={discountedPrice}
          currency={currency}
        />
      </ActionCardWrapper>
    </div>
  );
}
